import { Injectable } from '@angular/core';

import { TranslationService } from '@mirage-projects/cw-commons';
import { VolumeReportFormatted, VolumeReportProperties } from '@mirage-projects/cw-models';

const reportTranslations = {
    createdOn: 'VOLUME_REPORT.CREATED_ON',
    projectName: 'VOLUME_REPORT.PROJECT_NAME',
    name: 'VOLUME_REPORT.VOLUME_NAME',
    designSurface: 'SCANPROC.DESIGN_SURFACE',
    existingSurface: 'SCANPROC.EXISTING_SURFACE',
    totalVolume: 'VOLUME_REPORT.TOTAL_VOLUME',
    cutVolume: 'VOLUME_REPORT.CUT_VOLUME',
    fillVolume: 'VOLUME_REPORT.FILL_VOLUME',
    totalArea: 'VOLUME_REPORT.TOTAL_AREA',
    cutArea: 'VOLUME_REPORT.CUT_AREA',
    fillArea: 'VOLUME_REPORT.FILL_AREA',
    groundSampleDistance: 'VOLUME_REPORT.GROUND_SAMPLE_DISTANCE',
    originPointCoordinates: 'SCANPROC.ORIGIN_COORDINATES',
    calculationMethod: 'SCANPROC.CALC_METHOD',
    requiredHeightCornerCount: 'SCANPROC.HEIGHT_CORNER',
    designPlaneSettings: 'SCANPROC.DESIGN_PLANE',
} as const;

type GroupField = [string, string | null];

const categoriesTranslations = {
    volume: 'VOLUME_REPORT.VOLUME',
    area: 'VOLUME_REPORT.AREA',
    settings: 'VOLUME_REPORT.SETTINGS',
} as const;

/**
 * Service to help format the volume report as a textfile
 */
@Injectable({
    providedIn: 'root',
})
export class VolumeReportFormatService {
    constructor(private translationService: TranslationService) {}

    /**
     * Formats the volume report as a key value text file
     * @param file Volumer report data
     * @param projectName Name of the current project
     */
    formatFile(file: VolumeReportFormatted, projectName: string): string {
        const reportFields = this.prepareReportFields(file, projectName);
        return this.getFileFormatted(reportFields);
    }

    /**
     * Formats the volume report as a csv text file
     * @param file Volumer report data
     * @param projectName Name of the current project
     */
    formatFileAsCsv(file: VolumeReportFormatted, projectName: string): string {
        const formattedFile = this.formatFile(file, projectName);
        return this.formatAsCSV(formattedFile);
    }

    private prepareReportFields(file: VolumeReportFormatted, projectName: string): string[] {
        const groupedFields = this.groupFieldsByCategory(file);
        const genericGroupModified = this.formatGenericGroup(groupedFields.genericGroup, projectName);
        return [
            this.convertGroupToValidFormat(genericGroupModified),
            ...groupedFields.otherGroups.map((group) => this.convertGroupToValidFormat(group)),
        ];
    }

    private groupFieldsByCategory(file: VolumeReportFormatted): {
        genericGroup: GroupField[];
        otherGroups: GroupField[][];
    } {
        const initialGroups: {
            volumeGroup: GroupField[];
            areaGroup: GroupField[];
            settingsGroup: GroupField[];
            genericGroup: GroupField[];
        } = { volumeGroup: [], areaGroup: [], settingsGroup: [], genericGroup: [] };

        const fileGroups = Object.entries(file).reduce((groups, [key, value]) => {
            const translatedField = this.getTranslatedField(key as VolumeReportProperties, value);

            if (key.includes('Volume')) {
                groups.volumeGroup.push(translatedField);
            } else if (key.includes('Area')) {
                groups.areaGroup.push(translatedField);
            } else if (key.includes('Surface') || key.includes('created') || key === 'name') {
                groups.genericGroup.push(translatedField);
            } else {
                groups.settingsGroup.push(translatedField);
            }
            return groups;
        }, initialGroups);

        const { volumeGroup, areaGroup, settingsGroup, genericGroup } = fileGroups;

        return {
            genericGroup,
            otherGroups: [volumeGroup, areaGroup, settingsGroup],
        };
    }

    private formatGenericGroup(genericGroup: GroupField[], projectName: string): GroupField[] {
        const projectNameField = this.getTranslatedField('projectName', projectName);
        const nameIndex = genericGroup.findIndex(([key]) => key.includes('name'));
        const volumeNameField = genericGroup.splice(nameIndex, 1)[0];

        return [...genericGroup, projectNameField, volumeNameField];
    }

    private convertGroupToValidFormat(group: GroupField[]): string {
        return JSON.stringify(Object.fromEntries(group.map(([key, value]) => [key, value])));
    }

    private getTranslatedField(key: keyof VolumeReportFormatted, value: string): GroupField {
        const translationKey = reportTranslations[key] ?? key;
        const translatedPropertyName = this.translationService.translate(translationKey);

        const expr = /{{\s*([^{}]*)\s*}}/g; // Looks for {{key}}
        const interpolationData = expr.exec(translatedPropertyName);

        if (interpolationData) {
            const interpolationKey = interpolationData[1];
            const translationPair = { [interpolationKey]: value };
            return [this.translationService.translate(translationKey, translationPair), null];
        }

        return [translatedPropertyName, value];
    }

    private getFileFormatted(reportFields: string[]): string {
        const categories = this.getTranslatedCategories();
        return reportFields
            .map((field, index) => (index === 0 ? field : `[${categories[index - 1]}],${field}`))
            .flatMap((field) => field.split(','))
            .join('\n')
            .replace(/:null/, '')
            .replace(/[{}"]/g, '');
    }

    private formatAsCSV(fileContent: string): string {
        const newLine = '\n';
        const fileRows = fileContent
            .replace(' - ', ':')
            .split(newLine)
            .filter((row) => row.includes(':'));
        const dataRows = fileRows.map((row) => {
            const splitIndex = row.indexOf(':');
            return [row.slice(0, splitIndex), row.slice(splitIndex + 1)];
        });

        const headerColumns = dataRows.map((row) => row[0]).join(', ');
        const dataColumns = dataRows.map((row) => row[1]).join(', ');
        return `${headerColumns}${newLine}${dataColumns}`;
    }

    private getTranslatedCategories(): string[] {
        return Object.values(categoriesTranslations).map((category) => this.translationService.translate(category));
    }
}
